import { ReportComponentType } from '@/types';
import { DatasetQuery, ReportComponentConfig } from '@/types/api/modules/business-intelligence';
import { normalizeReportDescriptionConfiguration } from '@/utilities';

type RuntimeReportComponentConfig = Record<string, any> & {
    data?: unknown;
    dataSet?: Record<string, any>;
    query?: Partial<DatasetQuery>;
    type?: ReportComponentType;
    version?: number;
};

const DATA_COMPONENT_TYPES = new Set<ReportComponentType>([
    ReportComponentType.STATISTIC,
    ReportComponentType.MULTIPLE_STATISTICS,
    ReportComponentType.TABLE,
    ReportComponentType.MAP,
    ReportComponentType.GLOBE,
    ReportComponentType.FUNNEL_CHART,
    ReportComponentType.SANKEY_CHART,
    ReportComponentType.HEAT_MAP,
    ReportComponentType.SCATTER_PLOT,
    ReportComponentType.BAR_CHART,
    ReportComponentType.GAUGE_CHART,
    ReportComponentType.DONUT_CHART,
    ReportComponentType.PIE_CHART,
    ReportComponentType.AREA_CHART,
    ReportComponentType.LINE_CHART,
    ReportComponentType.RADAR_CHART,
    ReportComponentType.RADIAL_CHART,
    ReportComponentType.CHORD_CHART,
]);

const cloneConfig = (configuration?: unknown): RuntimeReportComponentConfig =>
    structuredClone((configuration ?? {}) as RuntimeReportComponentConfig);

const cleanMetric = (metric: DatasetQuery['metrics'][number]) =>
    metric?.field
        ? {
              field: metric.field,
              aggregation: metric.aggregation ?? 'sum',
              ...(metric.label ? { label: metric.label } : {}),
          }
        : undefined;

const cleanDimension = (dimension: DatasetQuery['dimensions'][number]) =>
    dimension?.field
        ? {
              field: dimension.field,
              ...(dimension.label ? { label: dimension.label } : {}),
              ...(dimension.sortBy ? { sortBy: dimension.sortBy } : {}),
          }
        : undefined;

const getRuntimeDataQuery = (data: unknown): Partial<DatasetQuery> => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {return {};}

    return data as Partial<DatasetQuery>;
};

const hasMeaningfulFilterExpression = (filters: unknown): filters is DatasetQuery['filters'] => {
    if (!filters || typeof filters !== 'object') {return false;}
    if (Array.isArray(filters)) {return filters.length > 0;}

    const filterObject = filters as Record<string, unknown>;
    const keys = Object.keys(filterObject);

    if (!keys.length) {return false;}

    const rules = filterObject.rules;
    const groups = filterObject.groups;
    const hasLegacyRules = Array.isArray(rules) && rules.length > 0;
    const hasLegacyGroups = Array.isArray(groups) && groups.length > 0;

    if (keys.every((key) => ['condition', 'rules', 'groups'].includes(key))) {
        return hasLegacyRules || hasLegacyGroups;
    }

    return true;
};

const normalizeDatasetQuery = (
    configuration: RuntimeReportComponentConfig,
    runtimeData: Partial<DatasetQuery> = {},
): DatasetQuery | undefined => {
    const datasetId = configuration.dataSet?.key ?? configuration.query?.datasetId ?? runtimeData.datasetId ?? '';
    const existingDatasetId = configuration.query?.datasetId ?? runtimeData.datasetId;
    const datasetChanged = Boolean(existingDatasetId && datasetId && existingDatasetId !== datasetId);
    const queryMetrics = configuration.query?.metrics?.length ? configuration.query.metrics : runtimeData.metrics;
    const queryDimensions = configuration.query?.dimensions?.length
        ? configuration.query.dimensions
        : runtimeData.dimensions;
    const queryFilters = hasMeaningfulFilterExpression(configuration.query?.filters)
        ? configuration.query?.filters
        : hasMeaningfulFilterExpression(runtimeData.filters)
          ? runtimeData.filters
          : undefined;
    const queryDateRange = configuration.query?.dateRange ?? runtimeData.dateRange;
    const queryLimit = configuration.query?.limit ?? runtimeData.limit;
    const queryOrderBy = configuration.query?.orderBy ?? runtimeData.orderBy;

    const metrics = datasetChanged ? [] : (queryMetrics ?? []).map(cleanMetric).filter(Boolean);
    const dimensions = datasetChanged ? [] : (queryDimensions ?? []).map(cleanDimension).filter(Boolean);

    const query: DatasetQuery = {
        datasetId,
        metrics: metrics as DatasetQuery['metrics'],
        dimensions: dimensions as DatasetQuery['dimensions'],
        ...(datasetChanged ? {} : queryFilters ? { filters: queryFilters } : {}),
        ...(datasetChanged ? {} : queryDateRange ? { dateRange: queryDateRange } : {}),
        ...(queryLimit ? { limit: queryLimit } : { limit: 100 }),
        ...(datasetChanged ? {} : queryOrderBy ? { orderBy: queryOrderBy } : {}),
    };

    if (!query.datasetId && !query.metrics.length && !query.dimensions.length && !query.filters) {
        return undefined;
    }

    return query;
};

export const normalizeReportComponentConfiguration = (
    configuration?: unknown,
    componentType?: ReportComponentType,
): Partial<ReportComponentConfig> => {
    const { data: runtimeData, ...rest } = cloneConfig(configuration);
    const runtimeQuery = getRuntimeDataQuery(runtimeData);
    const type = componentType ?? rest.type;
    const normalized: RuntimeReportComponentConfig = {
        ...rest,
        ...(type ? { type } : {}),
        version: rest.version ?? 1,
    };
    const legacyDatasetId = runtimeQuery.datasetId ?? normalized.query?.datasetId;

    if (normalized.dataSet && !normalized.dataSet.key && legacyDatasetId) {
        normalized.dataSet = {
            ...normalized.dataSet,
            key: legacyDatasetId,
        };
    }

    if (type === ReportComponentType.DESCRIPTION) {
        const descriptionConfiguration = normalizeReportDescriptionConfiguration(normalized as any);

        delete normalized.markdown;

        Object.assign(normalized, descriptionConfiguration);
    }

    if (type && DATA_COMPONENT_TYPES.has(type)) {
        const query = normalizeDatasetQuery(normalized, runtimeQuery);

        if (query) {
            normalized.query = query;
        } else {
            delete normalized.query;
        }
    } else {
        delete normalized.dataSet;
        delete normalized.query;
    }

    delete normalized.data;

    return normalized as Partial<ReportComponentConfig>;
};
